import React, { useEffect, useState } from 'react';
import { activityApi, projectApi } from '../services/api';
import api from '../services/api';

const adminApi = {
  trc: {
    list: () => api.get('/admin/trc'),
    create: (data) => api.post('/admin/trc', data),
    update: (code, data) => api.put(`/admin/trc/${code}`, data),
    deactivate: (code) => api.delete(`/admin/trc/${code}`),
  },
  projectActivities: {
    get: (projectId) => api.get(`/admin/project-activities/${projectId}`),
    link: (data) => api.post('/admin/project-activities', data),
    bulkLink: (data) => api.post('/admin/project-activities/bulk', data),
    unlink: (id) => api.delete(`/admin/project-activities/${id}`),
  },
};

export default function AdminSettingsPage() {
  const [tab, setTab] = useState('trc');

  return (
    <div>
      <div className="page-header">
        <h1>Admin Settings</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {['trc', 'activities', 'project-activities'].map(t => (
            <button key={t} className={`btn ${tab === t ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setTab(t)}>
              {t === 'trc' ? 'TRC Codes' : t === 'activities' ? 'Activities' : 'Project Activities'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'trc' && <TrcTab />}
      {tab === 'activities' && <ActivitiesTab />}
      {tab === 'project-activities' && <ProjectActivitiesTab />}
    </div>
  );
}

// ═══════════════════════════════════════════════
// TRC Codes Tab
// ═══════════════════════════════════════════════
function TrcTab() {
  const [trcs, setTrcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', description: '' });
  const [editCode, setEditCode] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => { loadTrcs(); }, []);

  const loadTrcs = async () => {
    try {
      const res = await adminApi.trc.list();
      if (res.data.success) setTrcs(res.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editCode) {
        await adminApi.trc.update(editCode, { description: form.description });
      } else {
        await adminApi.trc.create(form);
      }
      setShowForm(false); setEditCode(null); setForm({ code: '', description: '' });
      loadTrcs();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const toggleActive = async (trc) => {
    try {
      await adminApi.trc.update(trc.code, { active: !trc.active });
      loadTrcs();
    } catch (err) { alert('Error toggling status'); }
  };

  const filtered = showInactive ? trcs : trcs.filter(t => t.active);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <button className="btn btn-success" onClick={() => { setShowForm(true); setEditCode(null); setForm({ code: '', description: '' }); }}>
          + Add TRC Code
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h3>{editCode ? 'Edit TRC' : 'New TRC Code'}</h3>
            <button className="btn btn-sm btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
                <div className="form-group">
                  <label>Code</label>
                  <input className="form-control" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    required disabled={!!editCode} placeholder="e.g. REG, OVT" maxLength={10} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    required placeholder="e.g. Regular Hours" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary">{editCode ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Code</th><th>Description</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="4" className="empty-state">No TRC codes</td></tr>
              ) : filtered.map(t => (
                <tr key={t.code} style={{ opacity: t.active ? 1 : 0.5 }}>
                  <td><strong>{t.code}</strong></td>
                  <td>{t.description}</td>
                  <td><span className={`badge ${t.active ? 'badge-success' : 'badge-gray'}`}>{t.active ? 'Active' : 'Inactive'}</span></td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => { setForm({ code: t.code, description: t.description }); setEditCode(t.code); setShowForm(true); }}>Edit</button>
                    <button className={`btn btn-sm ${t.active ? 'btn-danger' : 'btn-success'}`} onClick={() => toggleActive(t)}>
                      {t.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// Activities Tab
// ═══════════════════════════════════════════════
function ActivitiesTab() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', description: '', category: '', billable: true });
  const [editId, setEditId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => { loadActivities(); }, []);

  const loadActivities = async () => {
    try {
      // Get all including inactive
      const res = await activityApi.list();
      if (res.data.success) setActivities(res.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await activityApi.update(editId, form);
      } else {
        await activityApi.create(form);
      }
      setShowForm(false); setEditId(null); setForm({ code: '', description: '', category: '', billable: true });
      loadActivities();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const toggleActive = async (act) => {
    try {
      await activityApi.update(act.id, { active: !act.active });
      loadActivities();
    } catch (err) { alert('Error toggling status'); }
  };

  const filtered = showInactive ? activities : activities.filter(a => a.active);
  const categories = [...new Set(activities.map(a => a.category).filter(Boolean))];

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <button className="btn btn-success" onClick={() => { setShowForm(true); setEditId(null); setForm({ code: '', description: '', category: '', billable: true }); }}>
          + Add Activity
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h3>{editId ? 'Edit Activity' : 'New Activity'}</h3>
            <button className="btn btn-sm btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>Code</label>
                  <input className="form-control" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    required disabled={!!editId} placeholder="e.g. DEV" maxLength={20} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input className="form-control" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                    required placeholder="e.g. Development / Coding" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input className="form-control" list="categories" value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value.toUpperCase() })}
                    placeholder="e.g. DEV, QA, MGMT" />
                  <datalist id="categories">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '8px 0' }}>
                <input type="checkbox" checked={form.billable} onChange={e => setForm({ ...form, billable: e.target.checked })} />
                Billable
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Code</th><th>Description</th><th>Category</th><th>Billable</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="empty-state">No activities</td></tr>
              ) : filtered.map(a => (
                <tr key={a.id} style={{ opacity: a.active ? 1 : 0.5 }}>
                  <td><strong>{a.code}</strong></td>
                  <td>{a.description}</td>
                  <td><span className="badge badge-info">{a.category || '—'}</span></td>
                  <td>{a.billable ? '✅ Yes' : '❌ No'}</td>
                  <td><span className={`badge ${a.active ? 'badge-success' : 'badge-gray'}`}>{a.active ? 'Active' : 'Inactive'}</span></td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => {
                      setForm({ code: a.code, description: a.description, category: a.category || '', billable: a.billable });
                      setEditId(a.id); setShowForm(true);
                    }}>Edit</button>
                    <button className={`btn btn-sm ${a.active ? 'btn-danger' : 'btn-success'}`} onClick={() => toggleActive(a)}>
                      {a.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// Project Activities Tab
// ═══════════════════════════════════════════════
function ProjectActivitiesTab() {
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [linkedActivities, setLinkedActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [projRes, actRes] = await Promise.all([projectApi.list(), activityApi.list()]);
      if (projRes.data.success) setProjects(projRes.data.data || []);
      if (actRes.data.success) setActivities(actRes.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadLinkedActivities = async (projectId) => {
    try {
      const res = await adminApi.projectActivities.get(projectId);
      if (res.data.success) {
        const linked = res.data.data || [];
        setLinkedActivities(linked);
        setSelectedActivities(linked.map(l => l.activity_id));
      }
    } catch (err) { console.error(err); }
  };

  const selectProject = (p) => {
    setSelectedProject(p);
    loadLinkedActivities(p.id);
  };

  const toggleActivity = (actId) => {
    setSelectedActivities(prev =>
      prev.includes(actId) ? prev.filter(id => id !== actId) : [...prev, actId]
    );
  };

  const selectAll = () => setSelectedActivities(activities.map(a => a.id));
  const deselectAll = () => setSelectedActivities([]);

  const saveLinks = async () => {
    try {
      await adminApi.projectActivities.bulkLink({
        projectId: selectedProject.id,
        activityIds: selectedActivities,
      });
      loadLinkedActivities(selectedProject.id);
      alert('Activities linked successfully!');
    } catch (err) { alert(err.response?.data?.message || 'Error saving'); }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
      {/* Project List */}
      <div className="card">
        <div className="card-header"><h3>Projects</h3></div>
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          {projects.length === 0 ? (
            <div className="card-body empty-state">No projects. Create projects first.</div>
          ) : projects.map(p => (
            <div key={p.id}
              onClick={() => selectProject(p)}
              style={{
                padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #e5e7eb',
                background: selectedProject?.id === p.id ? '#eff6ff' : 'transparent',
                borderLeft: selectedProject?.id === p.id ? '3px solid #2563eb' : '3px solid transparent',
              }}>
              <strong>{p.projectCode}</strong>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{p.projectName}</div>
              <span className={`badge ${p.status === 'ACTIVE' ? 'badge-success' : 'badge-gray'}`} style={{ fontSize: 11 }}>{p.status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Activity Selection */}
      <div className="card">
        <div className="card-header">
          <h3>{selectedProject ? `Activities for ${selectedProject.projectCode}` : 'Select a Project'}</h3>
          {selectedProject && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm btn-outline" onClick={selectAll}>Select All</button>
              <button className="btn btn-sm btn-outline" onClick={deselectAll}>Deselect All</button>
              <button className="btn btn-sm btn-primary" onClick={saveLinks}>Save</button>
            </div>
          )}
        </div>
        {!selectedProject ? (
          <div className="card-body empty-state">← Select a project to manage its activities</div>
        ) : (
          <div className="card-body">
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              {selectedActivities.length} of {activities.length} activities selected
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {activities.map(a => (
                <label key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer',
                  background: selectedActivities.includes(a.id) ? '#eff6ff' : '#fff',
                }}>
                  <input type="checkbox" checked={selectedActivities.includes(a.id)}
                    onChange={() => toggleActivity(a.id)} />
                  <div>
                    <strong>{a.code}</strong>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{a.description}</div>
                  </div>
                  {a.billable && <span className="badge badge-success" style={{ fontSize: 10, marginLeft: 'auto' }}>Billable</span>}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
